// src/rms/data/documentStorage.js
// ============================================================================
// Scans of customer documents
// ============================================================================
// These are photographs of driving licences, passports and insurance
// certificates — the most sensitive thing this product touches. Three rules
// follow from that, and they are enforced here as well as in the bucket policy
// (20260817110000), because one layer is not a control:
//
//   * The bucket is private. Nothing here ever produces a public URL. Viewing
//     mints a signed URL that expires in a minute and is never stored.
//   * The object path starts with the operator id, which is what the storage
//     policy checks. It is built here and never taken from user input.
//   * The customer's filename does NOT go in the path. Object paths surface in
//     logs and error reports, and "sarah-okafor-passport.jpg" leaks a name to
//     anyone who reads one. A uuid goes in the path; the real filename goes in
//     a database column behind RLS.
//
// On the local backend there is no Supabase Storage. Rather than pretend, files
// are held as object URLs for the session only — viewable while you are testing,
// gone on reload, and the UI says so. Writing base64 into localStorage would
// blow the ~5MB quota on the second passport photo and would break the standing
// rule that binary never goes in the database.
// ============================================================================

import { supabase } from "../config/supabaseClient";
import { isLocalBackend } from "./index";
import { uuid } from "./localAdapter";
import { createSuccessResponse, createErrorResponse } from "../utils/response";

export const DOCUMENT_BUCKET = "customer-documents";

/** Mirrors allowed_mime_types on the bucket. Kept in step by hand — the bucket
 *  is the enforcement; this exists to fail fast with a sentence a person can
 *  act on rather than a raw storage error. */
export const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];

/** Mirrors file_size_limit on the bucket. */
export const MAX_BYTES = 10 * 1024 * 1024;

/** For the file picker's accept attribute. */
export const ACCEPT_ATTRIBUTE = ".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf";

// Session-only store for the local backend. Never touched when Supabase is on.
const localFiles = new Map();

// ----------------------------------------------------------------------------

const extensionFor = (file) => {
  const fromName = /\.([a-z0-9]{2,5})$/i.exec(file.name || "");
  if (fromName) return fromName[1].toLowerCase();
  if (file.type === "application/pdf") return "pdf";
  return (file.type || "").split("/")[1] || "bin";
};

export function humanSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Checks a file before it leaves the browser.
 * @returns {string|null} an error to show, or null if the file is fine
 */
export function validateFile(file) {
  if (!file) return "No file chosen.";

  if (file.size > MAX_BYTES) {
    return `That file is ${humanSize(file.size)}. The limit is ${humanSize(MAX_BYTES)} — a photo of the document is enough, it doesn't need to be full resolution.`;
  }

  // Some browsers report an empty type for HEIC. Fall back to the extension
  // rather than refusing a perfectly normal iPhone photo.
  const byExtension = /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(file.name || "");
  if (file.type && !ACCEPTED_TYPES.includes(file.type) && !byExtension) {
    return "That file type isn't accepted. Use a photo (JPG, PNG, HEIC or WebP) or a PDF.";
  }

  return null;
}

// ----------------------------------------------------------------------------

/**
 * Uploads a scan and returns what belongs on the document row.
 *
 * @returns {Promise<{success, data: {storage_path, file_name, mime_type, size_bytes}, error}>}
 */
export async function uploadScan(file, { operatorId, customerId }) {
  const problem = validateFile(file);
  if (problem) return createErrorResponse(problem);
  if (!operatorId) return createErrorResponse("Missing operator — cannot file this document.");

  // Built here, never from user input: this path is what the storage policy
  // checks, so it is not somewhere to be relaxed about.
  const path = `${operatorId}/${customerId || "unfiled"}/${uuid()}.${extensionFor(file)}`;

  const meta = {
    storage_path: path,
    file_name: file.name || "scan",
    mime_type: file.type || null,
    size_bytes: file.size,
  };

  if (isLocalBackend) {
    localFiles.set(path, URL.createObjectURL(file));
    return createSuccessResponse({ ...meta, local_only: true });
  }

  const { error } = await supabase.storage
    .from(DOCUMENT_BUCKET)
    .upload(path, file, { contentType: file.type || undefined, upsert: false });

  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(meta);
}

/**
 * Files a captured signature, and returns the path it went to.
 *
 * SignaturePad hands back a data: URL from canvas.toDataURL(). Until now that
 * string went straight into rms_agreements.checkout_signature_path -- a base64
 * PNG in a text column named `_path`, which nothing in the app ever read back.
 * It was write-only, it contradicted the rule at the top of this file, and it
 * meant the one artefact proving a customer signed was the one artefact nobody
 * could look at.
 *
 * Same path convention as everything else in this bucket, built here and never
 * from user input, because that prefix is what the storage policy checks.
 *
 * Existing rows still hold their base64 and are left alone. There are no
 * backups, and rewriting historical evidence to tidy up a column is a worse
 * idea than living with two shapes and knowing which is which: a value starting
 * "data:" is an old one.
 */
export async function uploadSignature(dataUrl, { operatorId, customerId }) {
  if (!dataUrl) return createErrorResponse("No signature was captured.");
  if (!operatorId) return createErrorResponse("Missing operator — cannot file this signature.");

  let blob;
  try {
    // Small and already in memory; fetch() on a data: URL is the shortest
    // correct decode and does not need a secure context.
    blob = await (await fetch(dataUrl)).blob();
  } catch {
    return createErrorResponse("That signature could not be read.");
  }

  const file = new File([blob], "signature.png", { type: blob.type || "image/png" });
  return uploadScan(file, { operatorId, customerId });
}

/**
 * A short-lived URL for viewing one scan.
 *
 * Sixty seconds, and deliberately not cached anywhere: the whole point of a
 * private bucket is that a URL which escapes is worthless a minute later.
 */
export async function signedUrlFor(path, expiresInSeconds = 60) {
  if (!path) return createErrorResponse("This document has no scan attached.");

  if (isLocalBackend) {
    const url = localFiles.get(path);
    return url
      ? createSuccessResponse(url)
      : createErrorResponse(
          "That scan was only held for this browser session and has gone. Attach it again, or switch to the Supabase backend to store files properly."
        );
  }

  const { data, error } = await supabase.storage
    .from(DOCUMENT_BUCKET)
    .createSignedUrl(path, expiresInSeconds);

  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(data.signedUrl);
}

/**
 * A filename a person would recognise in their downloads folder.
 *
 * Object PATHS are uuids on purpose -- see the header: a path containing
 * "sarah-okafor-passport.jpg" leaks a name into every log line that mentions
 * it. But the path and the download name are different things, and the download
 * name is the one the customer reads. Leaving it as the uuid meant a signed
 * contract arrived called 947fc3ae-2b6e-48ea-9676-b6945c7839c5.pdf, which is
 * indistinguishable from junk six months later when it matters.
 */
export function niceFileName(parts, extension = "pdf") {
  const cleaned = parts
    .map((p) => String(p || "").trim())
    .filter(Boolean)
    .join(" - ")
    // Everything a filesystem or a Content-Disposition header would argue
    // about. Kept deliberately conservative: this string crosses Windows,
    // macOS, iOS and an HTTP header, and the strictest of those wins.
    .replace(/[\\/:*?"<>|\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);

  return `${cleaned || "document"}.${extension}`;
}

/**
 * A short-lived URL that downloads under a readable name.
 *
 * Same one-minute-ish expiry and the same private bucket as signedUrlFor; the
 * only difference is the `download` option, which sets Content-Disposition so
 * the browser saves it as `filename` rather than as the object's uuid.
 */
export async function downloadUrlFor(path, filename, expiresInSeconds = 120) {
  if (!path) return createErrorResponse("There is no file attached to this.");

  if (isLocalBackend) {
    const url = localFiles.get(path);
    return url
      ? createSuccessResponse(url)
      : createErrorResponse(
          "That file was only held for this browser session and has gone. Switch to the Supabase backend to store files properly."
        );
  }

  const { data, error } = await supabase.storage
    .from(DOCUMENT_BUCKET)
    .createSignedUrl(path, expiresInSeconds, { download: filename });

  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(data.signedUrl);
}

/** Deletes one scan. Safe to call with a path that no longer exists. */
export async function removeScan(path) {
  if (!path) return createSuccessResponse(true);

  if (isLocalBackend) {
    const url = localFiles.get(path);
    if (url) URL.revokeObjectURL(url);
    localFiles.delete(path);
    return createSuccessResponse(true);
  }

  const { error } = await supabase.storage.from(DOCUMENT_BUCKET).remove([path]);
  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(true);
}

/** Deletes several. Used to clean up scans staged but never saved. */
export async function removeScans(paths = []) {
  const real = paths.filter(Boolean);
  if (!real.length) return createSuccessResponse(true);

  if (isLocalBackend) {
    real.forEach((p) => removeScan(p));
    return createSuccessResponse(true);
  }

  const { error } = await supabase.storage.from(DOCUMENT_BUCKET).remove(real);
  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(true);
}

/**
 * Every scan belonging to an operator, erased.
 *
 * Called before rms_delete_my_operator, because deleting the database rows
 * would otherwise leave the actual images sitting in the bucket — which would
 * make "delete everything" a lie about precisely the files it matters most for.
 * Storage objects are not reachable from a SQL cascade, so this has to happen
 * from the client, first.
 */
export async function removeOperatorScans(operatorId) {
  if (!operatorId) return createSuccessResponse(true);
  if (isLocalBackend) {
    localFiles.forEach((url, path) => {
      if (path.startsWith(`${operatorId}/`)) {
        URL.revokeObjectURL(url);
        localFiles.delete(path);
      }
    });
    return createSuccessResponse(true);
  }

  // list() is not recursive, so walk the customer folders under the operator.
  const { data: folders, error: listError } = await supabase.storage
    .from(DOCUMENT_BUCKET)
    .list(operatorId, { limit: 1000 });

  if (listError) return createErrorResponse(listError.message);

  const paths = [];
  for (const folder of folders || []) {
    const { data: files } = await supabase.storage
      .from(DOCUMENT_BUCKET)
      .list(`${operatorId}/${folder.name}`, { limit: 1000 });
    (files || []).forEach((f) => paths.push(`${operatorId}/${folder.name}/${f.name}`));
  }

  return removeScans(paths);
}

export default {
  DOCUMENT_BUCKET,
  niceFileName,
  downloadUrlFor,
  ACCEPTED_TYPES,
  ACCEPT_ATTRIBUTE,
  MAX_BYTES,
  uploadScan,
  signedUrlFor,
  removeScan,
  removeScans,
  removeOperatorScans,
  validateFile,
  humanSize,
};
